import { makeAutoObservable } from 'mobx';
import { storageKeys, authUtils } from '@/modules/_core/constants/constants';
import { UserService } from '@/modules/_core/services/userService';
import { UserDataType } from '@/modules/_core/types/user-types';
import { UserRolesEnum } from '@sweatpals/shared';
import { setUser } from '@sentry/browser';

export class AuthStore {
	private isLoading = false;
	private token: string | null = null;
	private userData: UserDataType | null = null;
	private currentUserCommunityProfiles: UserDataType[] = [];

	constructor (private readonly usersService: UserService) {
		makeAutoObservable(this);

		this.initialize();
	}

	get isAuthLoading (): boolean {
		return this.isLoading;
	}

	get isAuth (): boolean {
		this.loadToken();

		return !!this.token;
	}

	get user (): UserDataType | null {
		return this.userData;
	}

	get isCommunityProfile (): boolean {
		return !!this.userData?.isCommunityProfile;
	}

	get communityProfiles (): UserDataType[] {
		return this.currentUserCommunityProfiles;
	}

	get userRole (): UserRolesEnum | undefined {
		return this.userData?.roleId;
	}

	public setUser (user: UserDataType) {
		this.userData = user;

		if (this.userData) {
			setUser({
				id: this.userData?.id,
				email: this.userData?.receiptsEmail ?? this.userData?.receiptsEmail,
				username: this.userData?.userName,
				login: this.userData?.login,
				fullName: this.userData?.fullName,
			});
		}
	}

	public async setAuth (access: string, refresh: string) {
		const changed = this.checkTokensChanged(access, refresh);

		if (changed) {
			this.setTokensToStorage(access, refresh);
			await this.initialize();
		}
	}

	public logout () {
		this.clearStorage();

		this.userData = null;
		this.token = null;
	}

	public checkTokensChanged (access: string, refresh: string) {
		const currentAccess = authUtils.getAccessTokenCookie();
		const currentRefresh = authUtils.getRefreshTokenCookie();

		return currentAccess !== access || currentRefresh !== refresh;
	}

	public setTokensToStorage (access: string, refresh: string) {
		authUtils.setAccessTokenCookie(access);
		authUtils.setRefreshTokenCookie(refresh);
	}

	public readStorage () {
		const access = authUtils.getAccessTokenCookie();
		const refresh = authUtils.getRefreshTokenCookie();

		if (access && refresh) {
			return { access, refresh };
		}

		return null;
	}

	public clearStorage (): void {
		authUtils.removeAccessTokenCookie();
		authUtils.removeRefreshTokenCookie();

		if (typeof window !== 'undefined') {
			localStorage.removeItem(storageKeys.access);
			localStorage.removeItem(storageKeys.refresh);
			localStorage.removeItem(storageKeys.userId);
		}
	}

	public async loadCurrentUser () {
		if (!this.token) {
			return;
		}

		const user = await this.usersService?.getCurrentUser();

		if (!user) {
			return;
		}

		this.setUser(user);

		await this.loadCommunityProfiles();
	}

	public async deleteCurrentUser () {
		if (!this.userData) {
			return;
		}

		await this.usersService.deleteUserFull(this.userData.id);
		this.logout();
	}

	private async loadCommunityProfiles () {
		if (!this.userData) {
			return;
		}

		const communityProfiles = await this.usersService?.getUserCommunityProfiles(this.userData.id);

		if (!communityProfiles) {
			return;
		}

		if (communityProfiles.list?.length) {
			this.currentUserCommunityProfiles = communityProfiles.list;
		}
	}

	private async initialize () {
		try {
			this.isLoading = true;

			if (!this.token) {
				this.loadToken();
			}

			if (this.token) {
				await this.loadCurrentUser();
			}
		} catch (e) {
			console.error(e);
		} finally {
			this.isLoading = false;
		}
	}

	private loadToken () {
		this.token = this.readStorage()?.access ?? null;
	}
}
